/**
 * Notification Navigation Helper
 * 
 * Provides consistent navigation logic for notifications across components
 * Handles routing to conversations with or without inbox context
 */

/**
 * Build conversation route parameters based on conversation ID and inbox ID
 * @param {number|string} accountId - Account ID
 * @param {number|string} conversationId - Conversation ID (display_id)
 * @param {number|string|null} inboxId - Inbox ID (optional)
 * @returns {Object} Vue Router route parameters
 */
export const buildConversationRouteParams = (accountId, conversationId, inboxId) => {
  if (inboxId) {
    return {
      name: 'conversation_through_inbox',
      params: {
        accountId,
        inbox_id: inboxId,
        conversation_id: conversationId,
      },
    };
  }

  return {
    name: 'inbox_conversation',
    params: {
      accountId,
      conversation_id: conversationId,
    },
  };
};

/**
 * Extract conversation ID from notification object
 * @param {Object} notification - Notification object
 * @returns {number|string|null} Conversation ID or null
 */
export const extractConversationId = (notification) => {
  return notification?.primary_actor?.id || null;
};

/**
 * Extract inbox ID from notification object
 * @param {Object} notification - Notification object
 * @returns {number|string|null} Inbox ID or null
 */
export const extractInboxId = (notification) => {
  return notification?.inbox_id || null;
};

/**
 * Build navigation route from notification object
 * @param {Object} notification - Notification object
 * @param {number|string} accountId - Account ID
 * @returns {Object} Vue Router route parameters
 */
export const buildRouteFromNotification = (notification, accountId) => {
  const conversationId = extractConversationId(notification);
  const inboxId = extractInboxId(notification);
  
  if (!conversationId) {
    return null;
  }

  return buildConversationRouteParams(accountId, conversationId, inboxId);
};

